import React from 'react'

/**
 * The login dialog's state for an email whose account was created with Google.
 *
 * The email check cannot tell whether the owner has since set a password, so
 * both paths are on screen: a password field for an owner who has, and a
 * password-setting email for one who has not. Opening another browser is the
 * quiet option, not the primary one.
 *
 * setupStatus: 'idle' | 'sending' | 'sent' | 'failed'
 */
export default function OauthOnlyContent({
  email,
  password,
  onPasswordChange,
  onLogin,
  loading,
  error,
  setupStatus,
  onSendSetup,
  onOpenInBrowser,
  onBack,
  t
}) {
  const setupBusy = setupStatus === 'sending' || setupStatus === 'sent'

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">{email}</p>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-amber-800">
          {t.oauth_only_message || 'This account was created with Google. If you have set a password, enter it to log in. If not, we will email you a link to set one.'}
        </p>
      </div>

      <form onSubmit={onLogin}>
        <div className="mb-3">
          <label htmlFor="oauth-only-password" className="block text-sm font-medium text-gray-700 mb-1">{t.password_label || 'Password'}</label>
          <input
            id="oauth-only-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={t.password_placeholder_login || 'Enter your password'}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        {error && <p role="alert" className="text-red-500 text-sm mb-3">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="w-full py-3 rounded-xl text-white font-medium text-sm transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#3366FF' }}
        >
          {loading ? (t.logging_in || 'Logging in...') : (t.login_button || 'Log in')}
        </button>
      </form>

      <button
        type="button"
        onClick={onSendSetup}
        disabled={setupBusy}
        className="w-full mt-3 py-3 rounded-xl text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 cursor-pointer"
      >
        {setupStatus === 'sending' ? (t.password_setup_sending || 'Sending...') : (t.send_password_setup || 'Email me a link to set a password')}
      </button>
      {setupStatus === 'sent' && (
        <p role="status" className="text-sm text-green-700 mt-2 text-center">{t.password_setup_sent || 'Email sent. After you set your password, come back here to log in.'}</p>
      )}
      {setupStatus === 'failed' && (
        <p role="alert" className="text-red-500 text-sm mt-2 text-center">{t.password_setup_failed || 'The email did not send. Try again.'}</p>
      )}

      <button
        type="button"
        onClick={onOpenInBrowser}
        className="w-full mt-3 py-2 text-sm text-blue-500 hover:text-blue-700 cursor-pointer"
      >
        {t.open_in_browser || 'Open in Browser'}
      </button>
      <button type="button" onClick={onBack} className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer">
        {t.back_to_email || 'Use a different email'}
      </button>
    </div>
  )
}
